// src/components/pages/HomePage.jsx
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import Header from '../layout/Header';
import MinimalFooter from '../layout/MinimalFooter';
import LazyImage from '../common/LazyImage';
import { blogService } from '../../services';
import {
  ProductDevIcon, TeamIcon, ConsultingIcon, QAIcon,
  CloudIcon, ImageIcon, AIIcon, VRIcon,
} from '../ui/Icons';

// ─── Shared primitives ────────────────────────────────────────────────────────

const Overline = ({ children }) => (
  <p className="text-xs font-semibold tracking-widest uppercase text-primary-dark mb-2">{children}</p>
);

const Panel = ({ active, children }) => (
  <div
    className={`absolute inset-0 overflow-y-auto md:overflow-hidden transition-opacity duration-200 ${
      active ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
    }`}
  >
    {children}
  </div>
);

const field = 'w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary/40 placeholder-gray-400 bg-white';

// ─── Compact contact form ─────────────────────────────────────────────────────

const CompactContact = () => {
  const [form, setForm] = useState({ name: '', email: '', company: '', message: '' });
  const [status, setStatus] = useState('idle');
  const [token, setToken] = useState('');
  const captchaRef = useRef(null);

  const handleChange = (e) => setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!token) return;
    setStatus('submitting');
    try {
      const res = await fetch('https://api.entelligentsia.in/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, captchaToken: token }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setStatus('success');
        setForm({ name: '', email: '', company: '', message: '' });
        setToken('');
        captchaRef.current?.reset();
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  if (status === 'success') {
    return (
      <div className="flex flex-col items-center justify-center h-48 text-center">
        <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mb-3">
          <svg className="w-6 h-6 text-green-600" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
        </div>
        <p className="font-semibold text-gray-800">Message sent!</p>
        <p className="text-sm text-gray-500 mt-1">We'll respond within 24 hours.</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-3">
      <div className="grid grid-cols-2 gap-3">
        <input name="name" value={form.name} onChange={handleChange} placeholder="Full Name *" required className={field} disabled={status === 'submitting'} />
        <input name="email" type="email" value={form.email} onChange={handleChange} placeholder="Email *" required className={field} disabled={status === 'submitting'} />
      </div>
      <input name="company" value={form.company} onChange={handleChange} placeholder="Company" className={field} disabled={status === 'submitting'} />
      <textarea name="message" value={form.message} onChange={handleChange} rows="3" placeholder="Tell us about your project *" required className={`${field} resize-none`} disabled={status === 'submitting'} />
      <ReCAPTCHA
        ref={captchaRef}
        sitekey="6Ldlg1orAAAAACNzogtTuDOF1ON4QmecDewi2UYr"
        size="compact"
        onChange={setToken}
        onExpired={() => setToken('')}
      />
      {status === 'error' && (
        <p className="text-xs text-red-500">Something went wrong — please try again.</p>
      )}
      <button
        type="submit"
        disabled={status === 'submitting' || !token}
        className="w-full bg-primary-dark text-white py-2.5 rounded-md text-sm font-medium hover:bg-primary transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
      >
        {status === 'submitting' ? 'Sending…' : 'Submit Inquiry'}
      </button>
      <p className="text-xs text-gray-400 text-center">We'll respond within 24 hours.</p>
    </form>
  );
};

// ─── Expertise panel (has its own tab state) ──────────────────────────────────

const ExpertisePanel = () => {
  const [tab, setTab] = useState('industries');

  const industries = [
    { name: 'Healthcare', img: '/images/healthcare.webp', items: ['EHR integrations', 'Telemedicine platforms', 'Medical imaging analysis', 'Patient engagement apps'] },
    { name: 'Sports Tech', img: '/images/sports_tech.webp', items: ['Performance analytics', 'Virtual training environments', 'Fan engagement platforms', 'Sports management systems'] },
    { name: 'Fintech', img: '/images/fintech.webp', items: ['Payment processing', 'Blockchain implementations', 'Wealth management platforms', 'Fraud detection & security'] },
  ];

  const tech = [
    { name: 'Cloud', skills: ['AWS', 'Azure', 'GCP', 'Kubernetes', 'Docker'] },
    { name: 'Image Analysis', skills: ['OpenCV', 'TensorFlow', 'PyTorch', 'Object Detection'] },
    { name: 'AI / ML', skills: ['Machine Learning', 'Deep Learning', 'NLP', 'Predictive Analytics'] },
    { name: 'Virtual Reality', skills: ['Unity', 'Unreal Engine', 'WebXR', '3D Modeling'] },
  ];

  return (
    <div className="h-full flex flex-col justify-center max-w-6xl mx-auto px-6 py-6">
      <Overline>Expertise</Overline>
      <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-5">Where We Specialize</h2>

      <div className="flex gap-1 mb-5 bg-gray-100 rounded-lg p-1 self-start">
        {['industries', 'technology'].map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-1.5 text-sm font-medium rounded-md capitalize transition-colors duration-150 ${
              tab === t ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === 'industries' ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {industries.map((ind) => (
            <div key={ind.name} className="bg-gray-50 rounded-xl overflow-hidden">
              <div className="h-28 overflow-hidden">
                <img src={ind.img} alt={ind.name} className="w-full h-full object-cover" loading="lazy" />
              </div>
              <div className="p-4">
                <h3 className="font-semibold text-gray-800 mb-2">{ind.name}</h3>
                <ul className="space-y-1.5">
                  {ind.items.map((f) => (
                    <li key={f} className="text-xs text-gray-500 flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-primary rounded-full flex-shrink-0 mt-1" />
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {tech.map((t) => (
            <div key={t.name} className="bg-gray-50 rounded-xl p-4">
              <h3 className="font-semibold text-sm text-gray-800 mb-3">{t.name}</h3>
              <div className="flex flex-wrap gap-1.5">
                {t.skills.map((s) => (
                  <span key={s} className="text-xs bg-white border border-gray-200 text-gray-600 px-2 py-0.5 rounded-full">
                    {s}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Home panel (hero + recent posts strip) ───────────────────────────────────

const HomePanel = ({ onSection, navigate }) => {
  const [recentPosts, setRecentPosts] = useState([]);

  useEffect(() => {
    blogService.getPosts({ showFuture: false, limit: 3 })
      .then((result) => setRecentPosts(result.posts))
      .catch(() => {});
  }, []);

  return (
    <div className="h-full flex flex-col max-w-6xl mx-auto px-6 py-6">
      {/* Hero row */}
      <div className="flex items-center gap-12 flex-1">
        {/* Left: text */}
        <div className="flex-1 min-w-0">
          <Overline>Elite Engineering</Overline>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 leading-tight mb-4">
            Built for<br /> Select Partnerships
          </h1>
          <p className="text-base md:text-lg text-gray-500 mb-7 leading-relaxed">
            We build high-performing engineering teams that create scalable, robust software for a curated roster of clients.
          </p>
          <div className="flex flex-wrap gap-3 mb-6">
            <button
              onClick={() => onSection('contact')}
              className="bg-primary-dark text-white px-6 py-2.5 rounded-md text-sm font-medium hover:bg-primary transition-colors"
            >
              Discuss Your Project
            </button>
            <button
              onClick={() => onSection('about')}
              className="text-gray-600 px-6 py-2.5 rounded-md text-sm font-medium border border-gray-200 hover:border-gray-400 transition-colors"
            >
              Our Approach
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {['Healthcare', 'Sports Tech', 'Fintech'].map((ind) => (
              <span key={ind} className="text-xs text-gray-400 bg-gray-100 px-3 py-1.5 rounded-full">
                {ind}
              </span>
            ))}
          </div>
        </div>

        {/* Right: hero image with decorative circle */}
        <div className="hidden md:block flex-shrink-0 relative" style={{ width: '320px', height: '320px' }}>
          <div className="absolute w-64 h-64 bg-primary-light rounded-full top-0 right-0 z-0 opacity-70" />
          <div className="absolute w-64 h-64 rounded-2xl overflow-hidden shadow-xl top-8 left-0 z-10">
            <picture>
              <source srcSet="/images/bridge.webp" type="image/webp" />
              <img
                src="/images/bridge.png"
                alt="Engineering excellence"
                className="w-full h-full object-cover"
                fetchPriority="high"
                decoding="async"
                loading="eager"
              />
            </picture>
          </div>
        </div>
      </div>

      {/* Recent posts strip */}
      {recentPosts.length > 0 && (
        <div className="mt-auto pt-5 border-t border-gray-100">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-semibold tracking-widest uppercase text-gray-400">Latest Writing</p>
            <button onClick={() => navigate('/blog')} className="text-xs text-primary-dark hover:text-primary font-medium transition-colors">
              View all →
            </button>
          </div>
          <div className="grid grid-cols-3 gap-4">
            {recentPosts.map((post) => (
              <button
                key={post.slug}
                onClick={() => navigate(`/blog/${post.slug}`)}
                className="flex items-center gap-3 text-left group"
              >
                {post.coverImage && (
                  <div className="w-14 h-14 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100">
                    <LazyImage
                      src={post.coverImage}
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                    />
                  </div>
                )}
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-gray-700 line-clamp-2 group-hover:text-primary-dark transition-colors leading-snug">
                    {post.title}
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5">{post.readTime} min read</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── HomePage ─────────────────────────────────────────────────────────────────

const HomePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeSection, setActiveSection] = useState(location.state?.section || 'home');

  const handleSection = (sectionId) => setActiveSection(sectionId);

  const services = [
    { title: 'Custom Product Dev', desc: 'End-to-end tailored software solutions.', Icon: ProductDevIcon },
    { title: 'Team Augmentation', desc: 'Specialized expertise for your team.', Icon: TeamIcon },
    { title: 'Tech Consulting', desc: 'Architecture and stack guidance.', Icon: ConsultingIcon },
    { title: 'Quality Assurance', desc: 'Comprehensive testing frameworks.', Icon: QAIcon },
  ];

  const processSteps = ['Discovery', 'Team Assembly', 'Integration', 'Execution'];

  const products = [
    { title: 'Forge CLI', desc: 'A disciplined coding harness for agents — install in one line.', img: '/images/forge-product.png', url: 'https://4ge.sh' },
    { title: 'WalkInto', desc: 'Interactive 3D virtual tours for any space.', img: '/images/walkinto-product.png', url: 'https://walkinto.in' },
    { title: 'HTFU', desc: 'Fitness tracking and workout planning.', img: '/images/htfu-product.png', url: 'https://htfu.in' },
    { title: 'SaveSquad', desc: 'Smart budgeting for university students.', img: '/images/savesquad-product.png', url: 'https://savesquad.in' },
  ];

  const openSource = [
    {
      name: 'Forge CLI',
      badge: 'npm · pi runtime · TUI',
      tagline: 'A coding harness for agents — engineering software, not vibe-coding',
      desc: "Launch it and you're in an interactive terminal — the pi coding-agent TUI plus Forge's own Orchestration TUI. Generates a project knowledge base, personas, and an SDLC pipeline, then drives them model-agnostically.",
      highlights: ['Interactive TUI: live sprint/task orchestration', 'Structured SDLC: plan → implement → review → validate → commit', 'Three-knob model routing (Heavy / Standard / Light)', 'Install in one line: curl -fsSL https://4ge.sh | sh'],
      github: 'https://github.com/Entelligentsia/forge-cli',
      page: '/open-source/forge-cli',
      img: '/images/forge_cli_os.webp',
      lqip: '/images/forge_cli_os.lqip.webp',
    },
    {
      name: 'Grove',
      badge: 'Rust · CLI + MCP',
      tagline: 'Ask where, get exact lines',
      desc: 'AST-aware code access for coding agents over a CLI and an MCP server — one symbol by exact bytes instead of grep and whole-file reads. 27 languages loaded at runtime.',
      highlights: ['Seven structural tools: defs, callers, source, refs…', 'Two surfaces, one tree-sitter engine', 'grove init wires into Claude Code, Cursor, Codex, Gemini CLI…', '~2.8× fewer context tokens than grep at the hardest rung'],
      github: 'https://github.com/Entelligentsia/grove',
      page: '/open-source/grove',
      img: '/images/grove_os.webp',
      lqip: '/images/grove_os.lqip.webp',
    },
    {
      name: 'Forge',
      badge: 'Claude Code Plugin',
      tagline: 'A self-enhancing AI engineering team for Claude Code',
      desc: 'Run it once — Forge reads your stack, generates project-specific agents, workflows, and a knowledge base, then deploys them as slash commands. Every sprint, it learns and gets sharper.',
      highlights: ['Adapts to your codebase — no config files', 'Multi-agent: Engineer, Supervisor, Architect', 'Self-learns with every sprint via retrospectives', 'Stack agnostic: Python, TS, Go, Ruby, Rust'],
      github: 'https://github.com/Entelligentsia/forge',
      page: '/open-source/forge',
      img: '/images/forge_os.webp',
      lqip: '/images/forge_os.lqip.webp',
    },
    {
      name: 'Skillforge',
      badge: 'Skill Packs',
      tagline: 'Curated skill packs for Claude Code and LLM agent systems',
      desc: 'Domain-specific skill libraries that extend Claude Code with deep expert knowledge. Install a pack and your AI pair programmer immediately masters your domain — design patterns, LLM integration, WebXR, Three.js, and more.',
      highlights: ['security-watchdog: auto-scans plugins for vulnerabilities', 'design-patterns: 10 skills, all 23 GoF + enterprise + DDD', 'llm-patterns (10) & harness-engineering (13)', 'doc-review, freshdesk-api, WebXR & Three.js'],
      github: 'https://github.com/Entelligentsia/skillforge',
      page: '/open-source/skillforge',
      img: '/images/skillforge_os.webp',
      lqip: '/images/skillforge_os.lqip.webp',
    },
  ];

  return (
    <div className="h-screen overflow-hidden flex flex-col bg-white text-gray-700">
      <Header
        onNavigate={(page) => page === 'blog' && navigate('/blog')}
        onSectionChange={handleSection}
        activeSection={activeSection}
        currentPage="home"
      />

      {/* Fixed spacer matching compact header height (py-2 + content ≈ 48px) */}
      <div className="h-12 flex-shrink-0" />

      <main className="flex-1 relative overflow-hidden">

        {/* ── Home ─────────────────────────────────────── */}
        <Panel active={activeSection === 'home'}>
          <HomePanel onSection={handleSection} navigate={navigate} />
        </Panel>

        {/* ── About ────────────────────────────────────── */}
        <Panel active={activeSection === 'about'}>
          <div className="h-full flex flex-col justify-center max-w-6xl mx-auto px-6 py-6">
            <Overline>About Us</Overline>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">Quality over quantity, always.</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
              <div className="space-y-3 text-sm text-gray-600 leading-relaxed">
                <p>At Entelligentsia Solutions LLP, we believe in quality over quantity. We deliberately limit our client roster to ensure each partnership receives our full attention and expertise.</p>
                <p>This exclusive approach allows us to deeply understand your business challenges, align with your vision, and build high-performing engineering teams dedicated to your success.</p>
                <p>Founded in Pune, India, we've assembled some of the region's top engineering talent, focused on creating scalable, robust solutions that stand the test of time.</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { title: 'Excellence', desc: 'The highest standards in every aspect of our work' },
                  { title: 'Partnership', desc: 'Lasting relationships built on trust and results' },
                  { title: 'Innovation', desc: 'New technologies to drive meaningful progress' },
                  { title: 'Precision', desc: 'Meticulous attention to every engineering detail' },
                ].map((v) => (
                  <div key={v.title} className="bg-gray-50 rounded-xl p-4">
                    <h4 className="font-semibold text-primary-dark text-sm mb-1">{v.title}</h4>
                    <p className="text-xs text-gray-500 leading-relaxed">{v.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Panel>

        {/* ── Services ─────────────────────────────────── */}
        <Panel active={activeSection === 'services'}>
          <div className="h-full flex flex-col justify-center max-w-6xl mx-auto px-6 py-6">
            <Overline>What We Do</Overline>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">Services</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              {services.map(({ title, desc, Icon }) => (
                <div key={title} className="bg-gray-50 rounded-xl p-4">
                  <div className="text-primary mb-2.5"><Icon /></div>
                  <h3 className="font-semibold text-sm text-gray-800 mb-1">{title}</h3>
                  <p className="text-xs text-gray-500 leading-relaxed">{desc}</p>
                </div>
              ))}
            </div>
            <div>
              <p className="text-xs text-gray-400 font-semibold tracking-widest uppercase mb-4">Our Process</p>
              <div className="flex items-center">
                {processSteps.map((step, i) => (
                  <React.Fragment key={step}>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <div className="w-7 h-7 rounded-full bg-primary flex items-center justify-center text-xs font-bold text-white flex-shrink-0">
                        {i + 1}
                      </div>
                      <span className="text-sm text-gray-600">{step}</span>
                    </div>
                    {i < processSteps.length - 1 && <div className="flex-1 h-px bg-gray-200 mx-4 min-w-4" />}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>
        </Panel>

        {/* ── Expertise ────────────────────────────────── */}
        <Panel active={activeSection === 'expertise'}>
          <ExpertisePanel />
        </Panel>

        {/* ── Products ─────────────────────────────────── */}
        <Panel active={activeSection === 'products'}>
          <div className="h-full overflow-y-auto">
            <div className="min-h-full flex flex-col justify-center max-w-5xl mx-auto px-6 py-8">
              <Overline>What We've Built</Overline>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">Products</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {products.map((p) => (
                  <div key={p.title} className="bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-md transition-shadow">
                    <div className="h-40 lg:h-48 bg-gray-100 overflow-hidden">
                      <img src={p.img} alt={p.title} className="w-full h-full object-cover" loading="lazy" />
                    </div>
                    <div className="p-5">
                      <h3 className="font-bold text-lg text-gray-800 mb-1">{p.title}</h3>
                      <p className="text-sm text-gray-500 mb-3 leading-relaxed">{p.desc}</p>
                      <a href={p.url} target="_blank" rel="noopener noreferrer" className="text-sm font-semibold text-primary-dark hover:text-primary transition-colors">
                        Visit Website →
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Panel>

        {/* ── Open Source ───────────────────────────────── */}
        <Panel active={activeSection === 'open-source'}>
          <div className="h-full overflow-y-auto">
            <div className="min-h-full flex flex-col justify-center max-w-6xl mx-auto px-6 py-8">
            <Overline>Open Source</Overline>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">Building in the Open</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {openSource.map((proj) => (
                <div key={proj.name} className="bg-white border border-gray-200 rounded-xl overflow-hidden flex flex-col hover:shadow-md transition-shadow">
                  {proj.img && (
                    <div className="h-40 lg:h-48 bg-gray-100 overflow-hidden relative">
                      <LazyImage
                        src={proj.img}
                        alt={proj.name}
                        className="w-full h-full object-cover"
                        lqipSrc={proj.lqip}
                      />
                    </div>
                  )}
                  <div className="p-6 flex flex-col flex-1">
                  <span className="inline-block text-xs font-semibold text-primary-dark bg-blue-50 px-2 py-0.5 rounded mb-3 self-start">
                    {proj.badge}
                  </span>
                  <h3 className="font-bold text-xl text-gray-800">{proj.name}</h3>
                  <p className="text-sm text-gray-400 mt-0.5 mb-3">{proj.tagline}</p>
                  <p className="text-sm text-gray-600 mb-4 leading-relaxed">{proj.desc}</p>
                  <ul className="space-y-1.5 mb-5 flex-1">
                    {proj.highlights.map((h) => (
                      <li key={h} className="text-xs text-gray-500 flex items-start gap-2">
                        <span className="text-primary mt-0.5 flex-shrink-0">✓</span>
                        {h}
                      </li>
                    ))}
                  </ul>
                  <div className="flex gap-2">
                    <button
                      onClick={() => navigate(proj.page)}
                      className="flex-1 bg-primary-dark text-white text-xs font-medium py-2 rounded-md hover:bg-primary transition-colors"
                    >
                      Learn More
                    </button>
                    <a
                      href={proj.github}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 text-center border border-gray-200 text-gray-600 text-xs font-medium py-2 rounded-md hover:bg-gray-50 transition-colors"
                    >
                      View on GitHub
                    </a>
                  </div>
                </div>
              </div>
            ))}
            </div>
            </div>
          </div>
        </Panel>

        {/* ── Contact ───────────────────────────────────── */}
        <Panel active={activeSection === 'contact'}>
          <div className="h-full flex items-center max-w-6xl mx-auto px-6 py-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 w-full">
              <div>
                <Overline>Get in Touch</Overline>
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">Start a Partnership</h2>
                <p className="text-sm text-gray-600 mb-6 leading-relaxed">
                  We work with a limited client roster to ensure exceptional quality. If you're interested in exploring a partnership, tell us about your project and we'll evaluate the fit.
                </p>
                <div className="space-y-4">
                  <div>
                    <p className="text-xs text-gray-400 font-semibold tracking-widest uppercase mb-1">Location</p>
                    <p className="text-sm text-gray-700">Pune, Maharashtra, India</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 font-semibold tracking-widest uppercase mb-1">Email</p>
                    <a href="mailto:contactus@entelligentsia.in" className="text-sm text-gray-700 hover:text-primary-dark transition-colors">
                      contactus@entelligentsia.in
                    </a>
                  </div>
                </div>
              </div>
              <CompactContact />
            </div>
          </div>
        </Panel>

      </main>

      <MinimalFooter />
    </div>
  );
};

export default HomePage;
